import { calculateMolarMass } from './chemistryCalculations';
import { toAsciiFormula } from './fuelsSustainability';
import { formatSig } from './gasLaws';
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';
import {
  findTransformation,
  pathwayFormula,
  pathwayTarget,
  type PathwayCase,
  type PathwayMolecule,
} from './synthesisDesign';

/**
 * Pathway yield Explore (CT-INTERACT): the learner types the starting mass and each step yield.
 * Every step is 1 : 1 along the carbon chain, so n(after step k) = n(start) × y1 × … × yk and the
 * product mass is n × M. Molar masses come from the Organic Builder formula and the shared
 * standard atomic weights; yields are the learner's own, starting from the practice values.
 */
export const START_MASS_FIELD: ModelField<'startMassG'> = {
  key: 'startMassG',
  label: 'Starting mass',
  unit: 'g',
  min: 0.1,
  max: 1000,
  step: 0.1,
};

export const DEFAULT_START_MASS_G = 10;
const PERCENT = 100;

export function yieldKey(index: number): string {
  return `yield${index + 1}`;
}

/** The fields for one route: starting mass, then one yield (1–100 %) per step. */
export function yieldExploreFields(entry: PathwayCase): ModelField[] {
  return [
    START_MASS_FIELD,
    ...entry.steps.map((item, index) => ({
      key: yieldKey(index),
      label: `Step ${index + 1} yield (${findTransformation(item.transformationId)?.label.toLowerCase() ?? item.transformationId})`,
      unit: '%',
      min: 1,
      max: 100,
      step: 0.1,
    })),
  ];
}

/** Starting values: 10 g and the route's practice step yields. */
export function defaultYieldValues(entry: PathwayCase): Record<string, number> {
  const values: Record<string, number> = { startMassG: DEFAULT_START_MASS_G };
  entry.steps.forEach((item, index) => {
    values[yieldKey(index)] = item.yieldPercent;
  });
  return values;
}

export interface YieldStepResult {
  name: string;
  formula: string;
  moles: number;
  massG: number;
  cumulativePercent: number;
}

export interface YieldExploreResult {
  startName: string;
  startMolarMass: number;
  startMoles: number;
  steps: YieldStepResult[];
  /** The learner's yield for each step, in route order (%). */
  stepYieldsPercent: number[];
  overallPercent: number;
  finalMassG: number;
  theoreticalMassG: number;
  worked: WorkedStep[];
}

function molarMassOf(item: PathwayMolecule): { formula: string; molarMass: number } {
  const formula = toAsciiFormula(pathwayFormula(item));
  const result = calculateMolarMass(formula);
  if (!result.value) throw new Error(`No molar mass is available for ${item.name}.`);
  return { formula, molarMass: result.value.molarMass };
}

const fmt = (value: number) => formatSig(value, 3);
const factor = (percent: number) => formatModelNumber(percent / PERCENT, 4);

/** Product moles and mass after every step for the learner's values. Throws on invalid input (fail closed). */
export function calculateYieldExplore(entry: PathwayCase, values: Record<string, number>): YieldExploreResult {
  const error = firstModelError(yieldExploreFields(entry), values);
  if (error) throw new Error(error);
  const start = molarMassOf(entry.start);
  const startMoles = values.startMassG / start.molarMass;
  const yields = entry.steps.map((_, index) => values[yieldKey(index)]);

  let moles = startMoles;
  let fraction = 1;
  const steps: YieldStepResult[] = [];
  const worked: WorkedStep[] = [
    {
      id: 'start',
      title: `Amount of ${entry.start.name}`,
      formula: 'n = m ÷ M',
      working: `n = ${fmt(values.startMassG)} g ÷ ${fmt(start.molarMass)} g mol⁻¹ = ${fmt(startMoles)} mol`,
      status: 'done',
    },
  ];
  entry.steps.forEach((item, index) => {
    const product = molarMassOf(item.product);
    const previous = moles;
    moles = previous * (yields[index] / PERCENT);
    fraction *= yields[index] / PERCENT;
    const massG = moles * product.molarMass;
    steps.push({
      name: item.product.name,
      formula: product.formula,
      moles,
      massG,
      cumulativePercent: fraction * PERCENT,
    });
    worked.push({
      id: `step-${index + 1}`,
      title: `Step ${index + 1}: ${findTransformation(item.transformationId)?.label ?? item.transformationId} → ${item.product.name}`,
      formula: 'n = n(before) × yield; m = n × M',
      working: `${fmt(previous)} mol × ${factor(yields[index])} = ${fmt(moles)} mol; ${fmt(moles)} mol × ${fmt(product.molarMass)} g mol⁻¹ = ${fmt(massG)} g`,
      status: 'done',
    });
  });

  const target = molarMassOf(pathwayTarget(entry));
  const overallPercent = fraction * PERCENT;
  const finalMassG = steps[steps.length - 1].massG;
  const theoreticalMassG = startMoles * target.molarMass;
  worked.push(
    {
      id: 'overall',
      title: 'Overall yield',
      formula: 'overall = y1 × y2 × … × 100 %',
      working: `${yields.map(factor).join(' × ')} × 100 % = ${fmt(overallPercent)} %`,
      status: 'done',
    },
    {
      id: 'check',
      title: 'Check against the theoretical mass',
      formula: 'actual ÷ (n(start) × M(target)) × 100 %',
      working: `${fmt(finalMassG)} g ÷ (${fmt(startMoles)} mol × ${fmt(target.molarMass)} g mol⁻¹ = ${fmt(theoreticalMassG)} g) × 100 % = ${fmt((finalMassG / theoreticalMassG) * PERCENT)} %`,
      status: 'done',
    },
  );

  return {
    startName: entry.start.name,
    startMolarMass: start.molarMass,
    startMoles,
    steps,
    stepYieldsPercent: yields,
    overallPercent,
    finalMassG,
    theoreticalMassG,
    worked,
  };
}

// Teaching goal (CT-INTERACT C7) ----------------------------------------------------------------

export const YIELD_QUESTION = 'Why does a route with good steps still lose so much product?';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the yield result, never the UI. */
export const YIELD_CHALLENGES: ExploreChallenge<YieldExploreResult>[] = [
  {
    id: 'half-overall',
    prompt: 'On a route with two or more steps, make the overall yield 50 % (between 48 % and 52 %).',
    hint: 'Choose a two-step target, then lower one step yield until the overall reads about 50 %.',
    isMet: (result) => result.steps.length >= 2 && result.overallPercent >= 48 && result.overallPercent <= 52,
    why: 'Two quite good steps already lose half the product: 70 % × 72 % ≈ 50 %. Each step only works on what the step before produced.',
  },
  {
    id: 'good-steps-poor-route',
    prompt: 'On a three- or four-step route, keep every step at 80 % or more but bring the overall yield below 60 %.',
    hint: 'Try 80 % for every step of a three-step route.',
    isMet: (result) =>
      result.steps.length >= 3 && result.stepYieldsPercent.every((value) => value >= 80) && result.overallPercent < 60,
    why: 'Yields multiply, they do not average: 0.80 × 0.80 × 0.80 = 51 %. A long route loses product even when every step looks good, so chemists prefer short routes.',
  },
  {
    id: 'twenty-grams',
    prompt: 'Change the starting mass so the route makes 20 g of the final product (19–21 g).',
    hint: 'Work backwards: n(product) = 20 g ÷ M, then divide by the overall yield and multiply by M(start).',
    isMet: (result) => result.finalMassG >= 19 && result.finalMassG <= 21,
    why: 'Plan backwards from the target: 20 g of ethanol is 20 ÷ 46.07 = 0.434 mol; at 95 % that needs 0.457 mol of ethene, which is 12.8 g. The product can weigh more than the start because the molar masses differ.',
  },
];
